import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  MOTION_GRAPHICS_CATEGORIES,
  MOTION_GRAPHICS_COLLECTION,
  MUSIC_COLLECTIONS,
  MUSIC_CONTRIBUTORS,
  MUSIC_QUALIFIED_KEYWORDS,
  SFX_COLLECTIONS,
  SFX_QUALIFIED_KEYWORDS,
  STOCK_VIDEO_CATEGORIES,
  STOCK_VIDEO_COLLECTION,
} from '@music-vine/setlist';
import { CACHE_TIMES } from '@/features/GlobalSearch/constants';
import { getTrendingSearches } from '@/features/GlobalSearch/utils/getTrendingSearches';
import { LUTS_MG_COLLECTION_MARKER } from '@/features/GlobalSearch/utils/mergeCollections';
import { trendingKeys } from '@/utils/queryKeys';

import type { GenericSearchResult } from '@/features/SearchPage/types';
import type { SearchResponse } from '@music-vine/setlist';

export function useTrendingData() {
  const { data, isLoading } = useQuery({
    queryKey: trendingKeys.searches(),
    queryFn: getTrendingSearches,
    staleTime: CACHE_TIMES.STALE_TIME,
    gcTime: CACHE_TIMES.GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const trendingMusicSearches = data?.search?.find(
    (collection: SearchResponse<GenericSearchResult>) =>
      collection.request_params?.collection_name === MUSIC_QUALIFIED_KEYWORDS
  );

  const trendingSfxSearches = data?.search?.find(
    (collection: SearchResponse<GenericSearchResult>) =>
      collection.request_params?.collection_name === SFX_QUALIFIED_KEYWORDS
  );

  // Hard code mg searches as we don't have qualified keywords for them
  const trendingMotionSearches = data?.search?.find(
    (collection: SearchResponse<GenericSearchResult>) =>
      collection.request_params?.collection_name === MOTION_GRAPHICS_CATEGORIES
  );

  const getCollectionByName = useCallback(
    (name: string) => {
      if (!data?.search?.length) {
        return;
      }

      return data.search.find(
        (collection: SearchResponse<GenericSearchResult>) =>
          collection.request_params?.collection_name === name
      );
    },
    [data?.search]
  );

  // Stock video categories
  const trendingStockVideoCategories = getCollectionByName(
    STOCK_VIDEO_CATEGORIES
  );

  const trendingData = {
    music: {
      searches: trendingMusicSearches,
      playlists: getCollectionByName(MUSIC_COLLECTIONS),
      artists: getCollectionByName(MUSIC_CONTRIBUTORS),
    },
    sfx: {
      searches: trendingSfxSearches,
      collections: getCollectionByName(SFX_COLLECTIONS),
    },
    motion: {
      searches: trendingMotionSearches,
      assets: getCollectionByName(MOTION_GRAPHICS_COLLECTION),
    },
    stock_footage: {
      categories: trendingStockVideoCategories,
      assets: getCollectionByName(STOCK_VIDEO_COLLECTION),
    },
    luts: {
      assets: getCollectionByName(LUTS_MG_COLLECTION_MARKER),
    },
  };

  return {
    trendingData,
    isLoading,
    getCollectionByName,
  };
}
